import React, { FC, useState, useEffect } from 'react';
import { FixedSizeList as List } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import dayjs from 'dayjs';
import { LOAD_ALL_PAGE_SIZE, MIN_SEARCH_QUERY_LENGTH } from 'utils/constants';

import { getApiUrl, PaginatedList } from 'utils/api';
import { useApi } from 'utils/api/useApi';
import usePrefix from 'utils/usePrefix';
import { InterventionPlanCategory, ReceivedInterventionPlanResponse } from 'utils/api/inbox';

import NoticeContainer from 'components/NoticeContainer';
import ListContainer from 'components/ListContainer';
import RowShort from 'components/RowShort';
import NoData from 'components/NoData';
import Loader from 'components/Loader';
import { ListWrapper } from 'components/ListContainer/styles';

import InterventionPlan from './InterventionPlan';
import PlanTab from './PlanTab';
import { TabsContainer } from './styles';

const url = getApiUrl('/interveners/me/intervention-plans');

const buildListUrl = (nameFilter: string, category: InterventionPlanCategory) => {
  const params = new URLSearchParams();
  params.set('category', category);
  if (nameFilter.length >= MIN_SEARCH_QUERY_LENGTH) {
    params.set('page', '0');
    params.set('size', LOAD_ALL_PAGE_SIZE.toString());
    params.set('name', nameFilter);
  }
  return `${url}?${params.toString()}`;
};

const InterventionPlans: FC = () => {
  const t = usePrefix('InterventionPlans');

  const [interventionPlanId, setInterventionPlanId] = useState<number | undefined>(undefined);
  const [nameFilter, setNameFilter] = useState('');
  const [category, setCategory] = useState<InterventionPlanCategory>('ALL');

  const { data, fetchData } = useApi<PaginatedList<ReceivedInterventionPlanResponse[]>>(
    buildListUrl(nameFilter, category),
    {
      method: 'GET',
    }
  );

  const handleCategoryChange = (next: InterventionPlanCategory) => {
    setCategory(next);
    setInterventionPlanId(undefined);
  };

  const handleFilterReset = () => {
    setNameFilter('');
  };

  useEffect(() => {
    if (!!data?.content.length && !interventionPlanId) setInterventionPlanId(data.content[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  if (!data) return <Loader label={t('loading')} />;

  return (
    <NoticeContainer>
      <ListContainer
        title={t('title')}
        setFilter={setNameFilter}
        filter={nameFilter}
        resetFilter={handleFilterReset}
        isSearchClearDisabled={nameFilter.length < 1}
      >
        <TabsContainer>
          <PlanTab value="ALL" text={t('tab_all')} activeCategory={category} onClick={handleCategoryChange} />
          <PlanTab value="SCHOOL" text={t('tab_school')} activeCategory={category} onClick={handleCategoryChange} />
          <PlanTab
            value="FAVORITE"
            text={t('tab_favorite')}
            activeCategory={category}
            onClick={handleCategoryChange}
          />
        </TabsContainer>
        {data.content.length > 0 && (
          <ListWrapper>
            <AutoSizer disableWidth>
              {({ height }: { height: number }) => (
                <List height={height} width="100%" itemCount={data.content.length} itemSize={64}>
                  {({ index, style }) => (
                    <RowShort
                      title={data.content[index].name}
                      subtitle={`${dayjs(data.content[index].receivedDate).format(
                        'DD-MM-YYYY'
                      )} / ${
                        data.content[index].readDate
                          ? dayjs(data.content[index].readDate).format('DD-MM-YYYY')
                          : t('not_read')
                      }`}
                      onClick={() => setInterventionPlanId(data.content[index].id)}
                      unread={!data.content[index].readDate}
                      selected={
                        !!interventionPlanId && data.content[index].id === interventionPlanId
                      }
                      style={style}
                    />
                  )}
                </List>
              )}
            </AutoSizer>
          </ListWrapper>
        )}
        {!interventionPlanId && <NoData title={t('empty_list')} />}
      </ListContainer>
      {!interventionPlanId ? (
        <NoData title={t('empty_list')} />
      ) : (
        <InterventionPlan id={interventionPlanId} fetchData={fetchData} />
      )}
    </NoticeContainer>
  );
};

export default InterventionPlans;
